import { useState } from 'react';
import { sendToHubSpot } from '@/utils/hubspot';

interface VideoLeadFormProps {
	onSubmit: () => void;
	onCancel: () => void;
}

export function VideoLeadForm({ onSubmit, onCancel }: VideoLeadFormProps) {
	const [formData, setFormData] = useState({
		firstName: '',
		lastName: '',
		email: '',
		phone: '',
		howDidYouHear: '',
		lookingForCoaching: '',
	});

	const [errors, setErrors] = useState<Record<string, string>>({});
	const [isLoading, setIsLoading] = useState(false);
	const [message, setMessage] = useState<string | null>(null);

	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
	) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));

		// Clear error when field is edited
		if (errors[name]) {
			setErrors((prev) => {
				const newErrors = { ...prev };
				delete newErrors[name];
				return newErrors;
			});
		}
	};

	const validateForm = () => {
		const newErrors: Record<string, string> = {};

		if (!formData.firstName.trim())
			newErrors.firstName = 'First name is required';
		if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';

		if (!formData.email.trim()) {
			newErrors.email = 'Email is required';
		} else if (!/\S+@\S+\.\S+/.test(formData.email)) {
			newErrors.email = 'Email is invalid';
		}

		if (!formData.phone.trim()) {
			newErrors.phone = 'Phone number is required';
		}

		if (!formData.howDidYouHear)
			newErrors.howDidYouHear = 'This field is required';
		if (!formData.lookingForCoaching)
			newErrors.lookingForCoaching = 'This field is required';

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (validateForm()) {
			setIsLoading(true);
			setMessage(null);
			try {
				await sendToHubSpot(
					{
						properties: [
							{ property: 'firstname', value: formData.firstName },
							{ property: 'lastname', value: formData.lastName },
							{ property: 'email', value: formData.email },
							{ property: 'phone', value: formData.phone },
							{
								property: 'how_did_you_hear_about_us',
								value: formData.howDidYouHear,
							},
							{
								property: 'looking_for_coaching',
								value: formData.lookingForCoaching,
							},
						],
					},
					4, // Hubspot List Id
				);
				setMessage('Form submitted successfully!');
				setFormData({
					firstName: '',
					lastName: '',
					email: '',
					phone: '',
					howDidYouHear: '',
					lookingForCoaching: '',
				});
				onSubmit();
			} catch (error) {
				console.error('Failed to submit form:', error);
				setMessage(
					`There was an error submitting your form: ${error.message}. Please try again.`,
				);
			} finally {
				setIsLoading(false);
			}
		}
	};

	return (
		<div className='bg-white rounded-xl p-6 md:p-8 shadow-xl max-w-2xl w-full mx-auto'>
			<div className='flex justify-between items-center mb-6'>
				<h2 className='text-2xl font-semibold text-gray-800'>
					Complete to Watch Video
				</h2>
				<button
					onClick={onCancel}
					className='text-gray-500 hover:text-gray-700'
					aria-label='Close'
				>
					<svg
						xmlns='http://www.w3.org/2000/svg'
						width='24'
						height='24'
						viewBox='0 0 24 24'
						fill='none'
						stroke='currentColor'
						strokeWidth='2'
						strokeLinecap='round'
						strokeLinejoin='round'
					>
						<line x1='18' y1='6' x2='6' y2='18'></line>
						<line x1='6' y1='6' x2='18' y2='18'></line>
					</svg>
				</button>
			</div>

			<form onSubmit={handleSubmit} className='space-y-4'>
				<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
					<div>
						<label
							htmlFor='firstName'
							className='block text-sm font-medium text-gray-700 mb-1'
						>
							First Name*
						</label>
						<input
							type='text'
							id='firstName'
							name='firstName'
							value={formData.firstName}
							onChange={handleChange}
							className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-[#FBB040] focus:border-[#FBB040] outline-none transition ${
								errors.firstName ? 'border-red-500' : 'border-gray-300'
							}`}
						/>
						{errors.firstName && (
							<p className='mt-1 text-sm text-red-600'>{errors.firstName}</p>
						)}
					</div>

					<div>
						<label
							htmlFor='lastName'
							className='block text-sm font-medium text-gray-700 mb-1'
						>
							Last Name*
						</label>
						<input
							type='text'
							id='lastName'
							name='lastName'
							value={formData.lastName}
							onChange={handleChange}
							className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-[#FBB040] focus:border-[#FBB040] outline-none transition ${
								errors.lastName ? 'border-red-500' : 'border-gray-300'
							}`}
						/>
						{errors.lastName && (
							<p className='mt-1 text-sm text-red-600'>{errors.lastName}</p>
						)}
					</div>
				</div>

				<div>
					<label
						htmlFor='email'
						className='block text-sm font-medium text-gray-700 mb-1'
					>
						Email Address*
					</label>
					<input
						type='email'
						id='email'
						name='email'
						value={formData.email}
						onChange={handleChange}
						className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-[#FBB040] focus:border-[#FBB040] outline-none transition ${
							errors.email ? 'border-red-500' : 'border-gray-300'
						}`}
					/>
					{errors.email && (
						<p className='mt-1 text-sm text-red-600'>{errors.email}</p>
					)}
				</div>

				<div>
					<label
						htmlFor='phone'
						className='block text-sm font-medium text-gray-700 mb-1'
					>
						Phone Number*
					</label>
					<input
						type='tel'
						id='phone'
						name='phone'
						value={formData.phone}
						onChange={handleChange}
						className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-[#FBB040] focus:border-[#FBB040] outline-none transition ${
							errors.phone ? 'border-red-500' : 'border-gray-300'
						}`}
					/>
					{errors.phone && (
						<p className='mt-1 text-sm text-red-600'>{errors.phone}</p>
					)}
				</div>

				<div>
					<label
						htmlFor='howDidYouHear'
						className='block text-sm font-medium text-gray-700 mb-1'
					>
						How did you hear about us?*
					</label>
					<select
						id='howDidYouHear'
						name='howDidYouHear'
						value={formData.howDidYouHear}
						onChange={handleChange}
						className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-[#FBB040] focus:border-[#FBB040] outline-none transition ${
							errors.howDidYouHear ? 'border-red-500' : 'border-gray-300'
						}`}
					>
						<option value=''>Select an option</option>
						<option value='social_media'>Social Media</option>
						<option value='search_engine'>Search Engine</option>
						<option value='friend'>Friend or Colleague</option>
						<option value='advertisement'>Advertisement</option>
						<option value='other'>Other</option>
					</select>
					{errors.howDidYouHear && (
						<p className='mt-1 text-sm text-red-600'>{errors.howDidYouHear}</p>
					)}
				</div>

				<div>
					<label
						htmlFor='lookingForCoaching'
						className='block text-sm font-medium text-gray-700 mb-1'
					>
						Are you looking for speaking coaching services?*
					</label>
					<select
						id='lookingForCoaching'
						name='lookingForCoaching'
						value={formData.lookingForCoaching}
						onChange={handleChange}
						className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-[#FBB040] focus:border-[#FBB040] outline-none transition ${
							errors.lookingForCoaching ? 'border-red-500' : 'border-gray-300'
						}`}
					>
						<option value=''>Select an option</option>
						<option value='yes'>Yes</option>
						<option value='no'>No</option>
						<option value='in_a_year_or_less'>Maybe in a year or less</option>
					</select>
					{errors.lookingForCoaching && (
						<p className='mt-1 text-sm text-red-600'>
							{errors.lookingForCoaching}
						</p>
					)}
				</div>

				<div className='pt-4'>
					<button
						type='submit'
						className='w-full bg-[#FBB040] text-white py-3 px-6 rounded-md hover:bg-[#FBB040]/90 transition-colors font-medium'
						disabled={isLoading}
					>
						{isLoading ? 'Submitting...' : 'Watch Video Now'}
					</button>
				</div>
			</form>
			{message && (
				<div
					className={`mt-4 text-center ${message.includes('successfully') ? 'text-green-600' : 'text-red-600'}`}
				>
					{message}
				</div>
			)}
		</div>
	);
}
